import React, { useState } from 'react';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings as SettingsIcon,
  LogOut,
  User,
  ClipboardPenLine,
  Images,
} from 'lucide-react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  useTheme,
  styled,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Logo from './Logo';
import TreatmentPlanCTA from './TreatmentPlanCTA';
import { useNavigation } from '../hooks/useNavigation';
import { useUser } from '../hooks/useUser';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetCurrentTreatmentPlan, setSentToPatientsIds } from '../redux/slices/treatmentPlan';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: 224, // default expanded width
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 224,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    overflow: 'visible',
  },
}));

const Sidebar = () => {
  const { goTo } = useNavigation();
  const dispatch = useDispatch();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const theme = useTheme();
  const { user, logout } = useUser();
  const currentPage = useLocation().pathname;
  const drawerWidth = collapsed ? 72 : 224;
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { id: 'patients', label: 'Patients', icon: Users, path: '/dashboard/patients' },
    { id: 'templates', label: 'Saved templates', icon: FileText, path: '/dashboard/templates' },
    { id: 'drafts', label: 'Drafts', icon: ClipboardPenLine, path: '/dashboard/drafts' },
    { id: 'media-library', label: 'Media library', icon: Images, path: '/dashboard/media-library' },
  ];

  const handleNavigate = (path: string) => {
    goTo(path);
  };
  const handleLogout = async () => {
    await logout();
    window.location.reload();
  };
  const handleNewTreatmentPlan = () => {
    dispatch(resetCurrentTreatmentPlan());
    dispatch(setSentToPatientsIds([]));
    handleNavigate('/dashboard/treatment-plan/select-patient');
  };
  const toggleCollapsed = () => setCollapsed(prev => !prev);
  return (
    <StyledDrawer
      variant="permanent"
      anchor="left"
      sx={{
        width: drawerWidth,
        '& .MuiDrawer-paper': { width: drawerWidth },
      }}
    >
      <Box
        sx={{
          py: 4,
          pl: collapsed ? 2 : 3,
          pr: collapsed ? 0 : 2,
          pb: 0,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: collapsed ? 56 : 120,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            cursor: 'pointer',
          }}
          onClick={() => handleNavigate('/dashboard')}
        >
          <Logo isCompact={collapsed} />
        </Box>
      </Box>

      {!collapsed && (
        <TreatmentPlanCTA
          onClick={handleNewTreatmentPlan}
          disabled={currentPage.includes('/treatment-plan')}
          fullWidth
        />
      )}

      <List sx={{ py: 3 }}>
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = currentPage === item.path;

          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                sx={{
                  py: 1.5,
                  px: collapsed ? 1.5 : 3,
                  borderRadius: 0,
                  minHeight: 48,
                  color: isActive ? theme.palette.primary.main : '#555A6A', // muted gray
                  backgroundColor: isActive
                    ? 'rgba(99, 102, 241, 0.08)' // light purple, adjust as needed
                    : 'transparent',
                  borderRight: isActive
                    ? `4px solid ${theme.palette.primary.main}`
                    : '4px solid transparent',
                  fontWeight: isActive ? 500 : 400,
                  '&:hover': {
                    backgroundColor: isActive
                      ? 'rgba(99, 102, 241, 0.08)'
                      : 'rgba(99, 102, 241, 0.04)',
                    color: theme.palette.primary.main,
                  },
                  transition: 'background 0.2s, color 0.2s',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 40,
                    color: isActive ? theme.palette.primary.main : '#555A6A',
                    transition: 'color 0.2s',
                  }}
                >
                  <Icon style={{ width: 20, height: 20 }} />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    sx={{
                      color: 'inherit',
                      fontWeight: isActive ? 500 : 400,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box
        sx={{
          mt: 'auto',
          p: collapsed ? 1.5 : 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: collapsed ? 0.5 : 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          onClick={() => setUserMenuOpen(!userMenuOpen)}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.main,
            }}
          >
            <User style={{ width: 16, height: 16 }} />
          </Avatar>
          {!collapsed && (
            <Box sx={{ ml: 2, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Doctor
              </Typography>
            </Box>
          )}
        </Box>

        {userMenuOpen && (
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Box
              onClick={() => handleNavigate('/dashboard/settings')}
              sx={{
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <SettingsIcon style={{ width: 16, height: 16, marginRight: 8 }} />
              <Typography variant="body2">Settings</Typography>
            </Box>
            <Box
              onClick={handleLogout}
              sx={{
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <LogOut style={{ width: 16, height: 16, marginRight: 8 }} />
              <Typography variant="body2">Logout</Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        onClick={toggleCollapsed}
        sx={{
          position: 'absolute',
          right: -18,
          bottom: 72,
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: '#fff',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 1201,
        }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={18} color={theme.palette.text.primary} strokeWidth={2.5} />
        ) : (
          <ChevronLeft size={18} color={theme.palette.text.primary} strokeWidth={2.5} />
        )}
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;
